import { z } from "zod";

// ─── Aetheria — 3D Scroll-Driven ──────────────────────────────────────────
//
// React Three Fiber + drei. A 3D primitive (ring / heart / orb) rotates and
// scales as the user scrolls. MVP ships with built-in primitive geometries
// instead of GLB files so the bundle stays small — preset GLB models are a
// Sprint 3 add-on.
//
// The R3F scene is dynamically imported (React.lazy) so the Three.js bundle
// (~200 KB gzipped) is only downloaded when this layout is selected.

const hexColor = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Geçersiz renk");

export const AETHERIA_MODEL_PRESETS = ["ring", "heart", "orb", "torus"] as const;
export type AetheriaModelPreset = typeof AETHERIA_MODEL_PRESETS[number];

export const AETHERIA_ROTATION_SPEEDS = ["slow", "medium", "fast"] as const;
export type AetheriaRotationSpeed = typeof AETHERIA_ROTATION_SPEEDS[number];

export const aetheriaConfigSchema = z.object({
  modelPreset: z.enum(AETHERIA_MODEL_PRESETS),
  rotationSpeed: z.enum(AETHERIA_ROTATION_SPEEDS),
  hero: z.object({
    label: z.string().max(120),
    tagline: z.string().max(240),
  }),
  story: z.object({
    label: z.string().max(60),
    headline: z.string().max(160),
  }),
  date: z.object({
    label: z.string().max(60),
  }),
  venue: z.object({
    label: z.string().max(60),
  }),
  rsvp: z.object({
    label: z.string().max(60),
    heading: z.string().max(80),
    closingPrefix: z.string().max(160),
  }),
  colors: z.object({
    /** Metalness color for the 3D model (PBR). */
    accent: hexColor,
    /** Background color (gradient base). */
    background: hexColor,
    /** Foreground text color. */
    text: hexColor,
  }),
});

export type AetheriaConfig = z.infer<typeof aetheriaConfigSchema>;

export const AETHERIA_DEFAULTS: AetheriaConfig = {
  modelPreset: "ring",
  rotationSpeed: "medium",
  hero: {
    label: "Aetheria",
    tagline: "{titleFirst} & {titleSecond}",
  },
  story: {
    label: "— Hikayemiz —",
    headline: "Bir yolculuğun\nzarif başlangıcı.",
  },
  date: {
    label: "● Tarih ●",
  },
  venue: {
    label: "● Mekan ●",
  },
  rsvp: {
    label: "● RSVP ●",
    heading: "Bizimle dans edin",
    closingPrefix: "{rsvpBy} tarihine kadar yanıtınız bizim için değerli",
  },
  colors: {
    accent: "#d4af37",
    background: "#0a0a14",
    text: "#f5f1e8",
  },
};

// Target frames-per-second for each speed.
// 96 frames ÷ fps = seconds per revolution:
//   slow   12fps → 8s/revolution  (natural jewellery pace)
//   medium 18fps → 5.3s/revolution (smooth showcase)
//   fast   24fps → 4s/revolution   (energetic)
// Minimum 12fps required for smooth animation (below that looks like ticking).
export const AETHERIA_ROTATION_RPM: Record<AetheriaRotationSpeed, number> = {
  slow:   12,   // fps
  medium: 18,
  fast:   24,
};
